import React from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { CheckCircleIcon, ChevronRightIcon, ClipboardIcon, CalendarIcon, ClockIcon, FileTextIcon, MailIcon, BellIcon } from 'lucide-react';
const ApplicationConfirmation = () => {
  const navigate = useNavigate();
  // In a real app, this data would come from the application submission response
  const applicationData = {
    id: 'APP-20230615-001',
    program: 'Master of Business Administration',
    university: 'UAE Business School',
    submittedDate: 'June 15, 2023',
    estimatedResponseTime: '2-3 weeks',
    nextSteps: ['Our admissions team will review your application', 'You may be contacted for an interview or additional information', 'A decision will be communicated to you via email']
  };
  return <Layout persona="Student" data-id="element-3254">
      <div className="max-w-3xl mx-auto" data-id="element-3255">
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-8 mb-8 text-center" data-id="element-3256">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-3257">
            <CheckCircleIcon className="w-8 h-8 text-green-500" data-id="element-3258" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2" data-id="element-3259">
            Application Submitted Successfully!
          </h1>
          <p className="text-gray-600 mb-6" data-id="element-3260">
            Your application for{' '}
            <span className="font-medium" data-id="element-3261">{applicationData.program}</span> at{' '}
            <span className="font-medium" data-id="element-3262">{applicationData.university}</span>{' '}
            has been received.
          </p>
          <div className="inline-block bg-gray-100 rounded-lg px-4 py-2 mb-6" data-id="element-3263">
            <p className="text-gray-700 font-medium" data-id="element-3264">
              Application ID: {applicationData.id}
            </p>
          </div>
          <div className="flex flex-col md:flex-row md:justify-center gap-4 mb-8" data-id="element-3265">
            <div className="flex items-center justify-center" data-id="element-3266">
              <CalendarIcon className="w-5 h-5 text-gray-500 mr-2" data-id="element-3267" />
              <span className="text-gray-700" data-id="element-3268">
                Submitted: {applicationData.submittedDate}
              </span>
            </div>
            <div className="flex items-center justify-center" data-id="element-3269">
              <ClockIcon className="w-5 h-5 text-gray-500 mr-2" data-id="element-3270" />
              <span className="text-gray-700" data-id="element-3271">
                Expected Response: {applicationData.estimatedResponseTime}
              </span>
            </div>
          </div>
          <div className="flex justify-center space-x-4" data-id="element-3272">
            <button onClick={() => navigate('/student/applications')} className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors flex items-center" data-id="element-3273">
              View My Applications
              <ChevronRightIcon className="w-4 h-4 ml-2" data-id="element-3274" />
            </button>
            <button onClick={() => navigate('/student')} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors" data-id="element-3275">
              Back to Dashboard
            </button>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8" data-id="element-3276">
          <div className="bg-primary-50 px-6 py-4 border-b border-primary-100" data-id="element-3277">
            <h2 className="text-lg font-semibold text-primary-800 flex items-center" data-id="element-3278">
              <ClipboardIcon className="w-5 h-5 mr-2 text-primary-500" data-id="element-3279" />
              What Happens Next?
            </h2>
          </div>
          <div className="p-6" data-id="element-3280">
            <ol className="relative border-l border-gray-200 ml-3 space-y-6" data-id="element-3281">
              {applicationData.nextSteps.map((step, index) => <li key={index} className="ml-6" data-id="element-3282">
                  <span className="absolute flex items-center justify-center w-6 h-6 bg-primary-100 rounded-full -left-3 ring-8 ring-white" data-id="element-3283">
                    <span className="text-primary-500 text-sm font-medium" data-id="element-3284">
                      {index + 1}
                    </span>
                  </span>
                  <p className="text-gray-700" data-id="element-3285">{step}</p>
                </li>)}
            </ol>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-3286">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6" data-id="element-3287">
            <div className="flex items-start" data-id="element-3288">
              <div className="bg-blue-100 p-3 rounded-full mr-4" data-id="element-3289">
                <BellIcon className="w-6 h-6 text-blue-500" data-id="element-3290" />
              </div>
              <div data-id="element-3291">
                <h3 className="font-semibold text-gray-800 mb-2" data-id="element-3292">
                  Stay Updated
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-3293">
                  We'll notify you about your application status via email and
                  in-app notifications.
                </p>
                <p className="text-sm text-gray-500" data-id="element-3294">
                  Make sure to check your email regularly and keep your contact
                  information up to date.
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6" data-id="element-3295">
            <div className="flex items-start" data-id="element-3296">
              <div className="bg-green-100 p-3 rounded-full mr-4" data-id="element-3297">
                <MailIcon className="w-6 h-6 text-green-500" data-id="element-3298" />
              </div>
              <div data-id="element-3299">
                <h3 className="font-semibold text-gray-800 mb-2" data-id="element-3300">Need Help?</h3>
                <p className="text-gray-600 mb-4" data-id="element-3301">
                  If you have any questions about your application, feel free to
                  contact our admissions team.
                </p>
                <a href="mailto:admissions@example.com" className="text-primary-500 hover:text-primary-600 font-medium" data-id="element-3302">
                  admissions@example.com
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-4" data-id="element-3303">
          <div className="flex items-start" data-id="element-3304">
            <FileTextIcon className="w-5 h-5 text-yellow-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-3305" />
            <div data-id="element-3306">
              <h3 className="font-medium text-yellow-800 mb-1" data-id="element-3307">
                Important Note
              </h3>
              <p className="text-sm text-yellow-700" data-id="element-3308">
                Your application is currently in "Pending Review" status. You
                can track the status of your application in the "My
                Applications" section of your dashboard.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>;
};
export default ApplicationConfirmation;